import React, { useState } from "react";
import { Button, Card, Col, Row, Nav, Modal } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { useIncome } from '../hooks/useIncome';
import { IncomeForm } from '../components/Income/IncomeForm';
import { IncomeStatsCards } from '../components/Income/IncomeStatsCards';
import { IncomeTable } from '../components/Income/IncomeTable';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactApexChart from "react-apexcharts";
import { ChartWrapper } from '../components/charts/ChartWrapper';
import { NoDataMessage } from '../components/common/NoDataMessage';


export default function Income() {
    const [showModal, setShowModal] = useState(false);
    const [selectedIncome, setSelectedIncome] = useState(null);
    const [filter, setFilter] = useState('all');
    const {
        incomes,
        sources,
        loading,
        stats,
        addIncome,
        updateIncome,
        deleteIncome
    } = useIncome();

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedIncome(null);
    };

    const handleSubmit = async (values, formikHelpers) => {
        const submitFn = selectedIncome ? updateIncome : addIncome;
        const result = await submitFn(values, formikHelpers);
        
        if (result.success) {
            toast.success(result.message);
            handleCloseModal();
        } else {
            toast.error(result.message);
        }
    };

    const filteredIncomes = incomes.filter(income => {
        if (filter === 'all') return true;
        if (filter === 'recurring') return income.recurring;
        return !income.recurring;
    });

    // Prepare data for source donut chart
    const getSourceData = () => {
        const sourceTotals = incomes.reduce((acc, income) => {
            acc[income.source] = (acc[income.source] || 0) + Number(income.amount);
            return acc;
        }, {});

        const hasData = Object.keys(sourceTotals).length > 0;

        return {
            series: hasData ? Object.values(sourceTotals) : [1],
            options: {
                chart: {
                    type: 'donut',
                },
                labels: hasData ? Object.keys(sourceTotals) : ['No Data'],
                colors: hasData ? 
                    ['#506fd9', '#85b6ff', '#33d685', '#0dcaf0', '#1c96e9', '#6e7985'] : 
                    ['#e9ecef'],
                legend: {
                    position: 'bottom',
                    show: hasData
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%',
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    showAlways: true,
                                    label: hasData ? 'Total' : 'No Data',
                                    formatter: function (w) {
                                        return hasData ? 
                                            `$${w.globals.seriesTotals.reduce((a, b) => a + b, 0).toFixed(2)}` : 
                                            'No Income Data';
                                    }
                                }
                            }
                        }
                    }
                }
            }
        };
    };

    // Prepare data for income trend area chart
    const getTrendData = () => {
        const monthlyData = incomes.reduce((acc, income) => {
            const month = new Date(income.date).toLocaleString('default', { month: 'short' });
            acc[month] = (acc[month] || 0) + Number(income.amount);
            return acc;
        }, {});

        const hasData = Object.keys(monthlyData).length > 0;

        return {
            series: [{
                name: 'Income',
                data: hasData ? Object.values(monthlyData) : [0]
            }],
            options: {
                chart: {
                    type: 'area',
                    height: 350,
                    toolbar: {
                        show: false
                    },
                    noData: {
                        text: 'No Income Data Available',
                        align: 'center',
                        verticalAlign: 'middle',
                        style: {
                            fontSize: '16px'
                        }
                    }
                },
                dataLabels: {
                    enabled: false
                },
                stroke: {
                    curve: 'smooth',
                    width: 2
                },
                xaxis: {
                    categories: hasData ? Object.keys(monthlyData) : ['No Data']
                },
                colors: ['#33d685'], // Different color from expense chart
                fill: {
                    type: 'gradient',
                    gradient: {
                        shadeIntensity: 1,
                        opacityFrom: 0.7,
                        opacityTo: 0.3,
                        stops: [0, 90, 100]
                    }
                },
                tooltip: {
                    y: {
                        formatter: (value) => `$${value.toFixed(2)}`
                    }
                }
            }
        };
    };

    const renderChart = (title, data, options, icon = "ri-pie-chart-line") => (
        <Card className="card-one">
            <Card.Header>
                <Card.Title as="h6">{title}</Card.Title>
            </Card.Header>
            <Card.Body style={{ height: '300px' }}>
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : incomes.length === 0 ? (
                    <NoDataMessage 
                        icon={icon}
                        message="No income data available"
                    />
                ) : (
                    <ChartWrapper
                        type="donut"
                        data={data}
                        options={options}
                        noDataIcon={icon}
                        noDataMessage="No income data available"
                    />
                )}
            </Card.Body>
        </Card>
    );

    const renderTrendChart = () => (
        <Card className="card-one">
            <Card.Header>
                <Card.Title as="h6">Income Trends</Card.Title>
            </Card.Header>
            <Card.Body style={{ height: '300px' }}>
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : incomes.length === 0 ? (
                    <NoDataMessage 
                        icon="ri-line-chart-line"
                        message="No income data available"
                    />
                ) : (
                    <ReactApexChart
                        options={getTrendData().options}
                        series={getTrendData().series}
                        type="area"
                        height="100%"
                    />
                )}
            </Card.Body>
        </Card>
    );

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <h4 className="main-title mb-0">Income</h4>
                    </div>
                    <Button 
                        variant="primary" 
                        onClick={() => setShowModal(true)}
                    >
                        Add New Income
                    </Button>
                </div>

                <IncomeStatsCards stats={stats} />

                <Card className="card-one mb-4">
                    <Card.Body>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <Nav className="nav-pills">
                                <Nav.Link
                                    active={filter === 'all'}
                                    onClick={() => setFilter('all')}
                                >All</Nav.Link>
                                <Nav.Link
                                    active={filter === 'recurring'}
                                    onClick={() => setFilter('recurring')}
                                >Recurring</Nav.Link>
                                <Nav.Link
                                    active={filter === 'one-time'}
                                    onClick={() => setFilter('one-time')}
                                >One-time</Nav.Link>
                            </Nav>
                        </div>

                        <IncomeTable 
                            incomes={filteredIncomes}
                            onDelete={deleteIncome}
                            onEdit={(income) => {
                                setSelectedIncome(income);
                                setShowModal(true);
                            }}
                        />
                    </Card.Body>
                </Card>

                <Row className="g-3">
                    <Col md={6}>
                        {renderChart('Income Distribution', getSourceData().series, getSourceData().options)}
                    </Col>
                    <Col md={6}>
                        {renderTrendChart()}
                    </Col>
                </Row>

                <Modal show={showModal} onHide={handleCloseModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {selectedIncome ? 'Edit Income' : 'Add New Income'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <IncomeForm
                            sources={sources}
                            initialValues={selectedIncome}
                            onSubmit={handleSubmit}
                            onCancel={handleCloseModal}
                        />
                    </Modal.Body>
                </Modal>

                <Footer />
            </div>
        </React.Fragment>
    );
} 